import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'features/rootReducer';
import { parseStoreQueryString } from 'modules/revenue';
import { getDefaultDashFormatInterval } from 'modules/utils/time';
import qs, { ParsedQs } from 'qs';
import { StoreType } from 'types/common';
import { ReferralRevenueData, ReferralRevenueFilter, ReferralRevenueSortType } from 'types/marketplace/revenue';
import { ExcelMultiDataSet } from 'types/react-export-excel';
import { referralRevenueDataToExcelMultiDataSet } from 'modules/excelTemplate/referral/referralRevenue';

export interface ReferralRevenueState {
  filter: ReferralRevenueFilter;
  referralListData: {
    data?: ReferralRevenueData;
    loading: boolean;
    error?: boolean;
  };
}

const [startDate, endDate] = getDefaultDashFormatInterval({
  gap: 0,
  unit: 'month',
  options: { useFirstDay: true },
});

const initialState: ReferralRevenueState = {
  filter: {
    startDate,
    endDate,
    keyword: '',
    storeTypes: [StoreType.CONNECT, StoreType.GAMEWEAR], // CONNECT(CONNECT + MD)
    sortType: ReferralRevenueSortType.REFERRAL_REVENUE_DESC,
    pageNo: 1,
    pageSize: 100,
  },
  referralListData: {
    loading: false,
  },
};

export const referralRevenueSlice = createSlice({
  name: 'revenueReferral',
  initialState: initialState,
  reducers: {
    resetFilter: (state: ReferralRevenueState) => {
      state.filter = initialState.filter;
    },
    search: (state: ReferralRevenueState, { payload }: PayloadAction<ReferralRevenueFilter>) => {
      state.filter = payload;
    },
    pageChange: (state: ReferralRevenueState, { payload: pageNo }: PayloadAction<number>) => {
      if (!pageNo) {
        state.filter.pageNo = 1;
      }
      state.filter.pageNo = pageNo;
    },
    initialize: (state: ReferralRevenueState, { payload }: PayloadAction<ParsedQs>) => {
      const { startDate, endDate, storeTypes, keyword, sortType } = payload;

      if (!startDate || !endDate) {
        state.filter = initialState.filter;
      } else {
        state.filter = {
          startDate,
          endDate,
          storeTypes: parseStoreQueryString(storeTypes, initialState.filter.storeTypes),
          keyword: keyword ?? initialState.filter.keyword,
          sortType: sortType !== undefined ? Number(sortType) : initialState.filter.sortType,
        } as unknown as ReferralRevenueFilter;
      }
    },
    setSortType: (state: ReferralRevenueState, { payload }: PayloadAction<ReferralRevenueSortType>) => {
      state.filter.sortType = payload;
    },
    loadData: (state: ReferralRevenueState) => {
      state.referralListData = { loading: true };
    },
    loadDataSuccess: (state: ReferralRevenueState, { payload }: PayloadAction<ReferralRevenueData>) => {
      state.referralListData = { loading: false, data: payload };
    },
    loadDataFailure: (state: ReferralRevenueState) => {
      state.referralListData = { loading: false, error: true };
    },
  },
});

export const referralRevenueActions = referralRevenueSlice.actions;
export const referralRevenueReducer = referralRevenueSlice.reducer;

export const selectReferralRevenueError = (state: RootState) =>
  state.referral.revenue.referralListData.error;
export const selectReferralRevenueIsLoading = (state: RootState) =>
  state.referral.revenue.referralListData.loading;
export const selectReferralRevenueFilter = (state: RootState) => state.referral.revenue.filter;
export const selectReferralRevenueQueryString = (state: RootState): string =>
  qs.stringify(state.referral.revenue.filter, { arrayFormat: 'repeat' });
export const selectReferralRevenueData = (state: RootState) =>
  state.referral.revenue.referralListData.data;
export const selectExcelMultiDataSet = (state: RootState): ExcelMultiDataSet[] | undefined => {
  const data = state.referral.revenue.referralListData.data;
  return data ? referralRevenueDataToExcelMultiDataSet(data) : undefined;
};
